
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, CheckCircle2, Tag, Code2 } from 'lucide-react'
import { ExtensionStatusBadge } from './ExtensionStatusBadge'
import type { Extension, ExtensionStatus } from '@/lib/types'

interface ExtensionsListProps {
  extensions: Extension[]
  isLoading: boolean
  currentPage: number
  totalPages: number
  itemsPerPage: number
  totalCount: number
  onPageChange: (page: number | ((prev: number) => number)) => void
  onItemsPerPageChange: (count: number) => void
  onStatusChange: (id: string, status: ExtensionStatus) => void
  onDelete: (id: string) => void
  onGenerateLicense: (id: string) => void
}

export function ExtensionsList({
  extensions,
  isLoading,
  currentPage,
  totalPages,
  itemsPerPage,
  totalCount,
  onPageChange,
  onItemsPerPageChange,
  onStatusChange,
  onDelete,
  onGenerateLicense,
}: ExtensionsListProps) {
  const formatPrice = (cents: number) => {
    if (cents === 0) return 'Free'
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0
    }).format(cents)
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="w-[300px]">Extension</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Developer</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
               [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><div className="h-4 w-48 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-20 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-16 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-8 w-8 ml-auto bg-muted animate-pulse rounded" /></TableCell>
                </TableRow>
              ))
            ) : extensions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No extensions found matching your criteria.
                </TableCell>
              </TableRow>
            ) : (
              extensions.map((ext) =>(
                <TableRow key={ext.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground flex items-center gap-2">
                        {ext.name}
                        {ext.isVerified && <CheckCircle2 className="h-3 w-3 text-blue-500" />}
                      </span>
                      <span className="text-xs text-muted-foreground truncate max-w-[250px]" title={ext.description || ''}>
                        {ext.description}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Tag className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">{ext.category || 'Uncategorized'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <ExtensionStatusBadge status={ext.status} />
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs">{formatPrice(ext.priceCents)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Code2 className="h-3 w-3" />
                      <span className="font-mono truncate max-w-[100px]">{ext.developerId}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => onStatusChange(ext.id, 'approved')}>
                          Approve Extension
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onStatusChange(ext.id, 'rejected')}>
                          Reject Extension
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onGenerateLicense(ext.id)}>
                          Generate License
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600" onClick={() => onDelete(ext.id)}>
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalCount > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} extensions
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(itemsPerPage)} onValueChange={(v) => onItemsPerPageChange(Number(v))}>
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>

                {/* Page numbers */}
                {totalPages <= 7 ? (
                  Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => onPageChange(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))
                ) : (
                  <>
                    {currentPage <= 3 ? (
                      <>
                        {Array.from({ length: 5 }, (_, i) => i + 1).map(page => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationEllipsis />
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(totalPages)} className="cursor-pointer">
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    ) : currentPage >= totalPages - 2 ? (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(1)} className="cursor-pointer">
                            1
                          </PaginationLink>
                        </PaginationItem>
                        <PaginationEllipsis />
                        {Array.from({ length: 5 }, (_, i) => totalPages - 4 + i).map(page => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                      </>
                    ) : (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(1)} className="cursor-pointer">
                            1
                          </PaginationLink>
                        </PaginationItem>
                        <PaginationEllipsis />
                        {Array.from({ length: 3 }, (_, i) => currentPage - 1 + i).map(page => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationEllipsis />
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(totalPages)} className="cursor-pointer">
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    )}
                  </>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}
    </div>
  )
}
